import { useEffect, useState } from 'react';
import { Sparkles, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CategoryCombobox } from '@/components/forms/CategoryCombobox';
import { Recommend as RecommendApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { TXN_MODES, DEFAULT_MODE_VALUE, decomposeMode, composeModeValue } from '@/lib/transactionModes';

// Stable category + MCC lists shared with the Transactions page. Kept here
// (vs prop-drilled) so the Edit dialog can be opened from any context.
// Phase 18 — 'General' is listed first AND used as the default for the
// new-transaction form. Phase 20 — removed 'Online' / 'Offline' /
// 'International' / 'EMI' from the category list because those are
// TRANSACTION MODES, not categories. They live in the Mode selector now.
export const TXN_FORM_CATEGORIES = [
  'General',
  'Dining', 'Travel', 'Flights', 'Hotels', 'Ferry', 'Cruise',
  'Fuel', 'FASTag', 'Tolls', 'Public transit',
  'Grocery', 'Pharmacy', 'Healthcare',
  'UPI', 'Wallets', 'Utilities', 'Rent', 'Insurance',
  'Education', 'Government payments', 'Gold & jewellery',
  'Shopping', 'Amazon', 'Flipkart', 'Electronics',
  'Entertainment', 'Subscriptions',
];

export const TXN_MCC_HINTS = [
  { mcc: '5814', label: 'Restaurants & dining' },
  { mcc: '5411', label: 'Grocery' },
  { mcc: '5541', label: 'Fuel — service stations' },
  { mcc: '4784', label: 'Tolls / FASTag / bridge fees' },
  { mcc: '4111', label: 'Public transit / ferry / commuter' },
  { mcc: '4112', label: 'Passenger railways' },
  { mcc: '4131', label: 'Bus lines' },
  { mcc: '3000-3299', label: 'Airlines' },
  { mcc: '7011', label: 'Hotels & lodging' },
  { mcc: '4722', label: 'Travel agencies' },
  { mcc: '6300', label: 'Insurance' },
  { mcc: '4900', label: 'Utilities' },
  { mcc: '9311', label: 'Tax payments' },
  { mcc: '6540', label: 'E-wallets top-up' },
  { mcc: '6051', label: 'Quasi-cash / wallets / forex' },
  { mcc: '5944', label: 'Jewellery' },
  { mcc: '8211', label: 'Schools / education' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

export const emptyTxnForm = (defaultCardId = '') => ({
  merchant: '',
  amount: '',
  category: 'General',
  mcc: '',
  card_id: defaultCardId,
  // Phase 20 — `mode_value` is the UI Mode selector value (e.g. 'online',
  // 'offline', 'emi', 'international', 'other'). `decomposeMode(mode_value)`
  // splits it into the three storage fields on submit.
  mode_value: DEFAULT_MODE_VALUE,
  date: todayIso(),
});

/**
 * Phase 20 — convenience: build the storage triple { mode, is_emi,
 * is_international } from a form's mode_value. Callers (Transactions page,
 * Recommend page) splat the result into the API payload.
 */
export const txnFormToApi = (form) => decomposeMode(form.mode_value || DEFAULT_MODE_VALUE);

/**
 * Phase 20 — convenience: hydrate an EditTransactionForm from a Transaction
 * row. Keeps the UI Mode label in sync with the stored mode/flag triple.
 */
export const apiToTxnForm = (tx, defaultCardId = '') => ({
  merchant: tx.merchant || '',
  amount: String(tx.amount ?? ''),
  category: tx.category || 'General',
  mcc: tx.mcc || '',
  card_id: tx.card_id || defaultCardId,
  mode_value: composeModeValue({
    mode: tx.mode,
    is_emi: tx.is_emi,
    is_international: tx.is_international,
  }),
  date: tx.date ? new Date(tx.date).toISOString().slice(0, 10) : todayIso(),
});

/**
 * Shared form body for adding/editing transactions. The parent owns the
 * dialog wrapper and the submit action — this just renders inputs and
 * surfaces a "better card" preview banner.
 *
 * Props:
 *   form, setForm                — controlled state object
 *   cards                        — wallet of selectable cards
 *   suggestions                  — category combobox list
 *   testidPrefix                 — testid prefix (defaults 'transactions')
 */
export function TransactionFormFields({ form, setForm, cards, suggestions, testidPrefix = 'transactions' }) {
  const [previewBest, setPreviewBest] = useState(null);
  const [showMcc, setShowMcc] = useState(!!form.mcc);

  useEffect(() => { if (form.mcc) setShowMcc(true); }, [form.mcc]);

  const refreshPreview = async (next) => {
    if (!next.amount || Number(next.amount) <= 0) { setPreviewBest(null); return; }
    try {
      // Phase 20 — Recommend API still uses `mode: online|offline` only.
      // Decompose the UI mode_value into the rules-engine signal.
      const { mode } = decomposeMode(next.mode_value || DEFAULT_MODE_VALUE);
      const r = await RecommendApi.run({
        amount: Number(next.amount),
        merchant: next.merchant,
        category: next.category,
        mcc: next.mcc || undefined,
        mode,
        include_ai: false,
      });
      setPreviewBest(r?.recommendation?.ranked?.[0] || null);
    } catch (_) {
      setPreviewBest(null);
    }
  };

  const patch = (delta, withPreview = true) => {
    const next = { ...form, ...delta };
    setForm(next);
    if (withPreview) refreshPreview(next);
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Merchant</Label>
        <Input
          value={form.merchant}
          onChange={(e) => patch({ merchant: e.target.value }, false)}
          placeholder="e.g. Amazon, IRCTC, BluFi, Paytm FASTag…"
          className="h-10 mt-1"
          data-testid={`${testidPrefix}-form-merchant`}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Amount</Label>
          <Input
            value={form.amount}
            onChange={(e) => patch({ amount: e.target.value.replace(/[^0-9]/g, '') })}
            placeholder="₹"
            className="h-10 mt-1 font-mono-num"
            data-testid={`${testidPrefix}-form-amount`}
          />
        </div>
        <div>
          <Label className="text-xs">Category</Label>
          <CategoryCombobox
            value={form.category}
            onChange={(v) => patch({ category: v })}
            suggestions={suggestions}
            testid={`${testidPrefix}-form-category`}
          />
        </div>
      </div>

      {/* Phase 20 — Mode selector. Orthogonal to Category: a "Dining" txn
          can be Online (Swiggy) OR Offline / POS (at the restaurant). The
          selected label decomposes into {mode, is_emi, is_international}
          on submit. Defaults to Online. */}
      <div>
        <Label className="text-xs">Mode</Label>
        <Select
          value={form.mode_value || DEFAULT_MODE_VALUE}
          onValueChange={(v) => patch({ mode_value: v })}
        >
          <SelectTrigger
            className="h-10 mt-1"
            data-testid={`${testidPrefix}-form-mode`}
          >
            <SelectValue placeholder="Select mode" />
          </SelectTrigger>
          <SelectContent>
            {TXN_MODES.map((m) => (
              <SelectItem key={m.value} value={m.value} data-testid={`${testidPrefix}-form-mode-${m.value}`}>
                <div className="flex flex-col items-start">
                  <span className="text-sm font-medium">{m.label}</span>
                  <span className="text-[10px] text-muted-foreground line-clamp-1">{m.description}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Date</Label>
        <Input
          type="date"
          value={form.date}
          max={todayIso()}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          className="h-10 mt-1"
          data-testid={`${testidPrefix}-form-date`}
        />
        <div className="text-[10px] text-muted-foreground mt-1">
          Period caps and milestone progress will be re-attributed to the chosen month.
        </div>
      </div>

      {/* Optional MCC */}
      <button
        type="button"
        onClick={() => setShowMcc((v) => !v)}
        className="w-full flex items-center justify-between text-[11px] uppercase tracking-wider text-muted-foreground hover:text-foreground pt-0.5 transition-colors"
        data-testid={`${testidPrefix}-form-mcc-toggle`}
      >
        <span>MCC code (optional · for niche categories like FASTag, Ferry, Insurance)</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', showMcc && 'rotate-180')} />
      </button>
      {showMcc && (
        <div>
          <Input
            value={form.mcc}
            onChange={(e) => patch({ mcc: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
            placeholder="e.g. 4784 (FASTag/Tolls)  ·  4111 (Ferry/transit)"
            className="h-9 font-mono-num text-sm"
            data-testid={`${testidPrefix}-form-mcc`}
          />
          <div className="mt-1.5 flex flex-wrap gap-1">
            {TXN_MCC_HINTS.map((h) => (
              <button
                key={h.mcc}
                type="button"
                onClick={() => patch({ mcc: h.mcc.split('-')[0] })}
                className="text-[10px] rounded-full border border-border bg-[hsl(var(--surface-2))] hover:border-accent/40 hover:bg-accent/5 px-2 py-0.5 font-mono-num"
                title={h.label}
                data-testid={`${testidPrefix}-form-mcc-hint-${h.mcc.split('-')[0]}`}
              >
                {h.mcc} · {h.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">Card used</Label>
        <Select value={form.card_id} onValueChange={(v) => patch({ card_id: v })}>
          <SelectTrigger className="h-10 mt-1" data-testid={`${testidPrefix}-form-card`}><SelectValue placeholder="Select a card" /></SelectTrigger>
          <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank} — {c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {previewBest && previewBest.card_id !== form.card_id && (
        <div className="rounded-lg border border-dashed border-accent/40 bg-accent/5 p-3 text-xs">
          <div className="flex items-center gap-1.5 text-accent font-medium"><Sparkles className="h-3.5 w-3.5" /> Smarter pick</div>
          <div className="mt-1 text-foreground/85">{previewBest.card_bank} {previewBest.card_name} would earn ₹{Math.round(previewBest.reward_value_inr)} ({previewBest.effective_rate_pct}%).</div>
        </div>
      )}
    </div>
  );
}
